// Playful — kid-birthday confetti paper-invitation catalog card.
//
// A festive, hand-stamped preview. Outer frame is a deckle/scallop edge (wavy
// border) in accent; inside sits the paper card with a confetti scatter in
// the corners (kept clear of the centre composition), an eyebrow that reads
// TUG'ILGAN KUN, a circular photo medallion with a stamped age badge, the
// child's name in the template font, and a small-caps date + venue caption.
// All colors come from LayoutCtx.
import { Photo, type LayoutCtx } from "./_shared";

// Deterministic confetti spec — only the corners and edges, never near centre.
type ConfettiShape = "rect" | "circle" | "tri" | "squiggle";
interface Spec {
  left: number;
  top: number;
  size: number;
  rot: number;
  color: string;
  shape: ConfettiShape;
}

function makeConfetti(accent: string, ink: string): Spec[] {
  const palette = [accent, ink, `${accent}aa`];
  const shapes: ConfettiShape[] = ["rect", "circle", "tri", "squiggle"];
  // Hand-picked positions — band along top/bottom & left/right margins, leaving
  // the centre 40-60% clear.
  const seeds: Array<[number, number]> = [
    [6, 8], [16, 4], [28, 10], [40, 6], [56, 8], [70, 4], [82, 10], [92, 6],
    [4, 22], [94, 22], [3, 40], [95, 42], [4, 60], [96, 62],
    [6, 86], [18, 92], [30, 86], [44, 94], [58, 88], [72, 92], [84, 86], [92, 92],
  ];
  return seeds.map(([l, t], i) => ({
    left: l,
    top: t,
    size: 4 + ((i * 3) % 4),
    rot: (i * 47) % 360,
    color: palette[i % palette.length],
    shape: shapes[i % shapes.length],
  }));
}

function Piece({ spec }: { spec: Spec }) {
  const base = {
    position: "absolute" as const,
    left: `${spec.left}%`,
    top: `${spec.top}%`,
    transform: `rotate(${spec.rot}deg)`,
    opacity: 0.75,
  };
  switch (spec.shape) {
    case "circle":
      return <div style={{ ...base, width: spec.size, height: spec.size, borderRadius: "50%", background: spec.color }} />;
    case "rect":
      return <div style={{ ...base, width: spec.size + 2, height: 3, background: spec.color }} />;
    case "tri":
      return (
        <div
          style={{
            ...base,
            width: 0,
            height: 0,
            borderLeft: `${spec.size / 2}px solid transparent`,
            borderRight: `${spec.size / 2}px solid transparent`,
            borderBottom: `${spec.size}px solid ${spec.color}`,
          }}
        />
      );
    case "squiggle":
      return (
        <svg width={spec.size + 6} height={4} viewBox="0 0 14 4" style={base} aria-hidden>
          <path d="M0 2 Q 3 0 6 2 T 12 2" stroke={spec.color} strokeWidth="1.2" fill="none" />
        </svg>
      );
  }
}

export function PlayfulLayout({ names, date, venue, accent, ink, bg, font, photo, photos }: LayoutCtx) {
  const confetti = makeConfetti(accent, ink);
  const heroPhoto = photo || photos[0];
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `repeating-linear-gradient(45deg, ${accent} 0 6px, ${bg} 6px 12px)`,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
          overflow: "hidden",
        }}
      >
        {/* Confetti scattered in corners/edges (not near centre) */}
        <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {confetti.map((spec, i) => (
            <Piece key={i} spec={spec} />
          ))}
        </div>

        {/* Eyebrow */}
        <div
          style={{
            position: "relative",
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TUG&apos;ILGAN KUN
        </div>

        {/* Photo medallion + age badge */}
        <div
          style={{
            position: "relative",
            width: "60%",
            aspectRatio: "1 / 1",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              overflow: "hidden",
              border: `3px solid ${accent}`,
              boxShadow: `0 6px 0 -2px ${ink}55`,
              background: `radial-gradient(circle at 50% 40%, ${accent}33, ${bg})`,
            }}
          >
            <Photo
              src={heroPhoto}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
              fallback={
                <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 50% 40%, ${accent}55, ${bg})` }} />
              }
            />
          </div>
          {/* Age badge — bottom-right */}
          <div
            style={{
              position: "absolute",
              right: "-2%",
              bottom: "-2%",
              width: "30%",
              aspectRatio: "1 / 1",
              borderRadius: "50%",
              background: accent,
              color: bg,
              display: "grid",
              placeItems: "center",
              fontFamily: font,
              fontSize: 22,
              fontWeight: 400,
              lineHeight: 1,
              boxShadow: `0 3px 0 -1px ${ink}66`,
              border: `2px solid ${ink}`,
            }}
          >
            7
          </div>
        </div>

        {/* Name */}
        <div
          style={{
            position: "relative",
            fontFamily: font,
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 400,
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            position: "relative",
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TUG&apos;ILGAN KUN · {date}
        </div>
        <div
          style={{
            position: "relative",
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
